import Util from "@/Master/Util";
import {RewardType} from "@/Core/Reward/RewardInfo";

export class DriveUtil {
    public static GoldStringMode = 0;

    // 将double/long 转为 long，这里TS统一用number
    public static ToAtomUnit(count: number): number {
        return this.DoubleToLong(count * 10000.0);
    }

    public static formatNumberWithCommas(num: number): string {
        let parts = num.toString().split('.');
        // 处理整数部分，添加千分位逗号
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    }

    public static ToNormalUnit(count: number): number {
        return count * 0.0001  ;
    }

    public static ScToDollar(sc: number): number {
        return sc; // 根据业务逻辑可做汇率转换
    }

    public static DoubleToLong(count: number): number {
        if (count >= 0) {
            return Math.floor(count + 0.00001);
        } else {
            return Math.floor(count - 0.00001);
        }
    }

    public static GetCoinString(type: RewardType, val: number): string {
        if (type === RewardType.GC) {
            return val.toString();
        }
        if (type === RewardType.SC) {
            return Util.NumberToString(DriveUtil.ToNormalUnit(val), 2);
        }
        return '';
    }

    public static GetSCString(val: number): string {
        return this.GetCoinString(RewardType.SC, val);
    }

    public static GetGCString(val: number): string {
        return this.GetCoinString(RewardType.GC, val);
    }

    public static ToGcKMBTUnitString(value: number, withGc: boolean = false): string {
        return withGc ? `GC ${Util.ToKMBTUnitString(value)}` : Util.ToKMBTUnitString(value);
    }

    public static ToGcString(value: number | string, withGc: boolean = false): string {
        let val = typeof value === 'number' ? value : parseFloat(value);
        if (DriveUtil.GoldStringMode === 1) {
            return withGc ? "GC " + Util.ToKMBTUnitString(val) : Util.ToKMBTUnitString(val);
        } else {
            return withGc ? "GC " + Util.ToThousandsString(val) : Util.ToThousandsString(val);
        }
    }

    public static ToScString(sc: number, withSc: boolean = false, kc: number = 2, isTrimZero: boolean = true): string {
        return (withSc ? "SC " : "") + Util.NumberToString(sc, kc, isTrimZero);
    }

    public static ToDollarString(value: number, needSymbol: boolean = true, isTrimZero: boolean = true, keepCount: number = 3): string {
        return (needSymbol ? "$" : "") + Util.NumberToString(value, keepCount, isTrimZero);
    }

    public static HideAccountName(email: string, hideCom: boolean): string {
        if (!email) return '';

        let message = '';
        if (!this.VerifyEmail(email, message)) {
            const str: string[] = [];
            if (email.length >= 4) {
                str.push(email.charAt(0));
                str.push(email.charAt(1));
                str.push('***');
                str.push(email.charAt(email.length - 2));
                str.push(email.charAt(email.length - 1));
            } else if (email.length > 2) {
                str.push(email.charAt(0));
                str.push(email.charAt(1));
                str.push('***');
            } else {
                str.push(email + '***');
            }
            return str.join('');
        }

        const atIndex = email.indexOf("@");
        const name = email.substring(0, atIndex);

        const str: string[] = [];

        if (name.length >= 4) {
            str.push(name.charAt(0));
            str.push(name.charAt(1));
            str.push('***');
            str.push(name.charAt(name.length - 2));
            str.push(name.charAt(name.length - 1));
        } else if (name.length === 3) {
            str.push(name.charAt(0));
            str.push(name.charAt(1));
            str.push('***');
            str.push(name.charAt(2));
        } else if (name.length === 2 || name.length === 1) {
            str.push(name);
            str.push('***');
        }

        if (!hideCom) {
            const atCom = email.substring(atIndex);
            str.push(atCom);
        }

        return str.join('');
    }

    public static HidePlayerName(playerName: string): string {
        if (!playerName || playerName.length <= 6) return playerName;
        const str: string[] = [];
        str.push(playerName.charAt(0));
        str.push(playerName.charAt(1));
        str.push(playerName.charAt(2));
        str.push('*****');
        str.push(playerName.charAt(playerName.length - 3));
        str.push(playerName.charAt(playerName.length - 2));
        str.push(playerName.charAt(playerName.length - 1));
        return str.join('');
    }

    private static readonly _regexMail = new RegExp("^\\w+.*@.*\\..*$");

    public static VerifyEmail(email: string, message: string): boolean {
        message = "Invalid email address.";
        if (!email) {
            return false;
        }

        if (email.indexOf(" ") >= 0) {
            message = "Invalid email format. No spaces allowed.";
            return false;
        }

        if (!this._regexMail.test(email)) {
            return false;
        }

        return true;
    }

    public static isDigit(char: string): boolean {
        return /^[0-9]$/.test(char);
    }

    public static isLetter(char: string): boolean {
        return /^[a-zA-Z]$/.test(char);
    }

    public static VerifyZipCode(code: string, countryCode: string): boolean {
        if (!code) {
            return false;
        }

        let limitLength: number;

        if (!countryCode) {
            return false;
        }

        switch (countryCode) {
            case "US":
            case "DE":
            case "FR":
                limitLength = 5;
                if (code.length !== limitLength) {
                    return false;
                }
                for (let i = 0; i < limitLength; i++) {
                    if (!this.isDigit(code[i])) {
                        return false;
                    }
                }
                return true;

            case "AU":
                limitLength = 4;
                if (code.length !== limitLength) {
                    return false;
                }
                for (let i = 0; i < limitLength; i++) {
                    if (!this.isDigit(code[i])) {
                        return false;
                    }
                }
                return true;

            case "GB":
                const codeSplitList = code.split(' ');
                if (codeSplitList.length === 2) {
                    if (
                        codeSplitList[1].length === 3 &&
                        this.isDigit(codeSplitList[1][0]) &&
                        this.isLetter(codeSplitList[1][1]) &&
                        this.isLetter(codeSplitList[1][2])
                    ) {
                        if (codeSplitList[0].length === 2) {
                            if (this.isLetter(codeSplitList[0][0]) && this.isDigit(codeSplitList[0][1])) {
                                return true;
                            }
                        } else if (codeSplitList[0].length === 3) {
                            if (
                                this.isLetter(codeSplitList[0][0]) &&
                                ((this.isDigit(codeSplitList[0][1]) && this.isDigit(codeSplitList[0][2])) ||
                                    (this.isLetter(codeSplitList[0][1]) && this.isDigit(codeSplitList[0][2])) ||
                                    (this.isDigit(codeSplitList[0][1]) && this.isLetter(codeSplitList[0][2])))
                            ) {
                                return true;
                            }
                        } else if (codeSplitList[0].length === 4) {
                            if (
                                this.isLetter(codeSplitList[0][0]) &&
                                this.isLetter(codeSplitList[0][1]) &&
                                this.isDigit(codeSplitList[0][2]) &&
                                (this.isLetter(codeSplitList[0][3]) || this.isDigit(codeSplitList[0][3]))
                            ) {
                                return true;
                            }
                        }
                    }
                }
                return false;

            case "JP":
                limitLength = 8;
                if (code.length !== limitLength) {
                    return false;
                }
                const codeSplitList2 = code.split('-');
                if (codeSplitList2.length === 2 && codeSplitList2[0].length === 3) {
                    for (const part of codeSplitList2) {
                        for (const char of part) {
                            if (!this.isDigit(char)) {
                                return false;
                            }
                        }
                    }
                    return true;
                }
                return false;

            case "CA":
                // 正则表达式：加拿大邮编格式
                // const pattern = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
                const pattern = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
                return pattern.test(code);
            default:
                return code.length >= 2 && code.length <= 20;
        }
    }

}